/**
 * Auto Starter Dispute Flow
 * 
 * Complete end-to-end dispute journey for Auto Starter users.
 * Executed AFTER onboarding (report already imported).
 * 
 * Flow:
 * 1. Analysis (if not done) → Loading
 * 2. Recommendation Screen (Factual vs Metro 2)
 * 3. Selected Dispute Path Screen
 * 4. Review & Finalize
 * 5. Generate Package / Continue
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useReportData } from '../../context/ReportDataContext';
import { metro2Service } from '../../services/metro2Service';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Import the new screens
import DisputePathRecommendationScreen, { getRecommendation } from './DisputePathRecommendationScreen';
import FactualDisputeSelectionScreen from './FactualDisputeSelectionScreen';
import Metro2DisputeSelectionScreen from './Metro2DisputeSelectionScreen';
import ReviewFinalizeStep from './ReviewFinalizeStep';
import ConsumerIdentityStep from './ConsumerIdentityStep';

// Flow steps
const FLOW_STEPS = {
  ANALYZING: 'analyzing',
  RECOMMENDATION: 'recommendation',
  FACTUAL_SELECTION: 'factual_selection',
  METRO2_SELECTION: 'metro2_selection',
  CONSUMER_IDENTITY: 'consumer_identity',
  REVIEW_FINALIZE: 'review_finalize',
  GENERATING: 'generating',
  COMPLETE: 'complete',
};

const AutoStarterDisputeFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const { reportData, loading: reportLoading } = useReportData();
  
  // Flow state
  const [currentStep, setCurrentStep] = useState(FLOW_STEPS.ANALYZING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Analysis state
  const [analysisResults, setAnalysisResults] = useState(null);
  const [findings, setFindings] = useState([]);
  
  // Selection state
  const [selectedPath, setSelectedPath] = useState(null); // 'factual' or 'metro2'
  const [disputeSubType, setDisputeSubType] = useState(null); // For factual: 'general', 'identity_theft', 'data_breach'
  const [breachSource, setBreachSource] = useState(null); // For data breach
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemDetails, setItemDetails] = useState({});
  
  // Consumer identity state
  const [consumerIdentity, setConsumerIdentity] = useState(null);
  
  // Package state
  const [packages, setPackages] = useState([]);
  const [letterPreviews, setLetterPreviews] = useState({});
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [roundData, setRoundData] = useState(null);
  
  // Run analysis on mount
  useEffect(() => {
    if (reportData && !reportLoading) {
      runAnalysis();
    }
  }, [reportData, reportLoading]);
  
  // Run analysis using the dispute engine
  const runAnalysis = async () => {
    setLoading(true);
    setCurrentStep(FLOW_STEPS.ANALYZING);
    
    try {
      // Call the analyze endpoint
      // Use import_provider from report data (set during import), fallback to provider, then 'identityiq'
      const provider = reportData?.import_provider || reportData?.provider || 'identityiq';
      const result = await metro2Service.analyzeReport(
        user?.user_id || user?.id,
        'auto_starter',
        provider,
        reportData,
        token
      );
      
      if (result.success) {
        // Build lookup maps for tradelines and inquiries
        const tradelines = reportData?.tradelines || [];
        const inquiries = reportData?.inquiries || [];
        
        const tradelineMap = {};
        tradelines.forEach(t => {
          const id = t.tradeline_id || t.id;
          if (id) tradelineMap[id] = t;
        });
        
        const inquiryMap = {};
        inquiries.forEach(i => {
          const id = i.inquiry_id || i.id;
          if (id) inquiryMap[id] = i;
        });
        
        // Enrich findings with creditor information
        const enrichedFindings = (result.findings || []).map(finding => {
          const entityId = finding.context?.entity_id;
          const tradeline = tradelineMap[entityId];
          const inquiry = inquiryMap[entityId];
          
          // Get creditor name from tradeline or inquiry
          const creditorName = tradeline?.creditor_name || 
                              tradeline?.account_name ||
                              inquiry?.company_name ||
                              inquiry?.creditor_name ||
                              inquiry?.creditor ||
                              finding.context?.creditor_name;
          
          // Get account number if available
          const accountNumber = tradeline?.account_number_masked || 
                               tradeline?.account_number ||
                               inquiry?.inquiry_date;
          
          return {
            ...finding,
            creditor_name: creditorName,
            account_number: accountNumber,
            // Mark type based on source
            type: inquiry ? 'inquiry' : 'account',
            // Include original entity data for reference
            entity_data: tradeline || inquiry,
          };
        });
        
        setAnalysisResults({
          findings: enrichedFindings,
          summary: result.summary || {},
          round_id: result.round_id,
        });
        setFindings(enrichedFindings);
        setRoundData({ id: result.round_id });
        
        // Move to recommendation step
        setCurrentStep(FLOW_STEPS.RECOMMENDATION);
      } else {
        throw new Error(result.error || 'Analysis failed');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message);
      toast.error('Failed to analyze report. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle path selection from recommendation screen
  const handlePathSelection = (path) => {
    setSelectedPath(path);
    
    if (path === 'factual') {
      setCurrentStep(FLOW_STEPS.FACTUAL_SELECTION);
    } else {
      setCurrentStep(FLOW_STEPS.METRO2_SELECTION);
    }
  };
  
  // Handle factual dispute selection completion
  const handleFactualSelection = (selectionData) => {
    setDisputeSubType(selectionData.subType);
    setBreachSource(selectionData.selectedBreach);
    setSelectedItems(
      findings.filter(f => selectionData.includedItems.includes(f.id))
    );
    setItemDetails(selectionData.itemDetails);
    
    // Move to review
    setCurrentStep(FLOW_STEPS.REVIEW_FINALIZE);
  };
  
  // Handle Metro 2 dispute selection completion
  const handleMetro2Selection = (selectionData) => {
    setSelectedItems(
      findings.filter(f => selectionData.includedItems.includes(f.id))
    );
    setItemDetails(selectionData.itemDetails);
    
    // Move to review
    setCurrentStep(FLOW_STEPS.REVIEW_FINALIZE);
  };
  
  // Handle document upload
  const handleDocumentUpload = async (file) => {
    // Upload to backend
    try {
      const result = await metro2Service.uploadResponseDocument(
        roundData?.id,
        file,
        {
          description: file.name,
          document_type: 'supporting',
          source: 'consumer',
          user_id: user?.user_id || user?.id,
          user_name: user?.name || 'User',
        },
        token
      );
      
      if (result.success) {
        setUploadedDocuments(prev => [...prev, {
          id: result.document_id,
          name: file.name,
          type: file.type,
          size: file.size,
        }]);
        toast.success('Document uploaded');
      }
    } catch (err) {
      toast.error('Failed to upload document');
    }
  };
  
  // Handle document removal
  const handleDocumentRemove = async (docId) => {
    try {
      await metro2Service.deleteDocument(docId, token);
      setUploadedDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document removed');
    } catch (err) {
      toast.error('Failed to remove document');
    }
  };
  
  // Handle finalize
  const handleFinalize = async () => {
    setCurrentStep(FLOW_STEPS.GENERATING);
    setLoading(true);
    
    try {
      // Build consumer context with identity data
      const consumerContext = {
        dispute_type: selectedPath,
        dispute_sub_type: disputeSubType,
        breach_source: breachSource,
        item_details: itemDetails,
        // Include consumer identity for letter generation
        consumer_identity: consumerIdentity ? {
          full_name: [
            consumerIdentity.firstName,
            consumerIdentity.middleName,
            consumerIdentity.lastName,
            consumerIdentity.suffix,
          ].filter(Boolean).join(' '),
          first_name: consumerIdentity.firstName,
          middle_name: consumerIdentity.middleName,
          last_name: consumerIdentity.lastName,
          suffix: consumerIdentity.suffix,
          ssn_last4: consumerIdentity.ssn,
          date_of_birth: consumerIdentity.dateOfBirth,
          address: {
            street: consumerIdentity.currentAddress?.street,
            city: consumerIdentity.currentAddress?.city,
            state: consumerIdentity.currentAddress?.state,
            zip: consumerIdentity.currentAddress?.zipCode,
          },
          phone: consumerIdentity.phone,
          email: consumerIdentity.email,
        } : null,
      };
      
      // Create the dispute round with selected items and consumer identity
      const createResult = await metro2Service.createRound({
        userId: user?.user_id || user?.id,
        accountIds: selectedItems.filter(i => i.type !== 'inquiry').map(i => i.context?.entity_id || i.id),
        inquiryIds: selectedItems.filter(i => i.type === 'inquiry').map(i => i.context?.entity_id || i.id),
        strategyMode: 'bureau_package',
        provider: reportData?.import_provider || 'identityiq',
        reportData: reportData,
        consumerData: consumerContext.consumer_identity,
      }, token);
      
      if (createResult.round_id) {
        // Render letters after round is created
        await metro2Service.renderLetters(createResult.round_id, token);
        
        toast.success('Dispute package generated!');
        setCurrentStep(FLOW_STEPS.COMPLETE);
        
        // Navigate to round detail page
        navigate(`/disputes/${createResult.round_id}`);
      } else {
        throw new Error(createResult.error || 'Failed to create round');
      }
    } catch (err) {
      console.error('Finalize error:', err);
      toast.error(err.message || 'Failed to generate dispute package');
      setCurrentStep(FLOW_STEPS.REVIEW_FINALIZE);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle back navigation
  const handleBack = () => {
    switch (currentStep) {
      case FLOW_STEPS.RECOMMENDATION:
        navigate('/disputes');
        break;
      case FLOW_STEPS.FACTUAL_SELECTION:
      case FLOW_STEPS.METRO2_SELECTION:
        setCurrentStep(FLOW_STEPS.RECOMMENDATION);
        break;
      case FLOW_STEPS.REVIEW_FINALIZE:
        if (selectedPath === 'factual') {
          setCurrentStep(FLOW_STEPS.FACTUAL_SELECTION);
        } else {
          setCurrentStep(FLOW_STEPS.METRO2_SELECTION);
        }
        break;
      default:
        navigate('/disputes');
    }
  };
  
  // Loading state
  if (loading && currentStep === FLOW_STEPS.ANALYZING) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Analyzing Your Report</h2>
          <p className="text-slate-400">Finding issues and building recommendations...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error && currentStep === FLOW_STEPS.ANALYZING) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Analysis Failed</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/disputes')}
              className="px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-600"
            >
              Go Back
            </button>
            <button
              onClick={runAnalysis}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Generating state
  if (currentStep === FLOW_STEPS.GENERATING) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Generating Dispute Package</h2>
          <p className="text-slate-400">Creating your dispute letters...</p>
        </div>
      </div>
    );
  }
  
  // Render current step
  switch (currentStep) {
    case FLOW_STEPS.RECOMMENDATION:
      return (
        <DisputePathRecommendationScreen
          analysisResults={analysisResults}
          onSelectPath={handlePathSelection}
          onBack={handleBack}
        />
      );
    
    case FLOW_STEPS.FACTUAL_SELECTION:
      return (
        <FactualDisputeSelectionScreen
          findings={findings}
          onContinue={(result) => {
            // Result is an object: { subType, selectedBreach, breachFollowUp, includedItems, itemDetails }
            // Get the actual finding objects for the included item IDs
            const selectedFindings = findings.filter(f => result.includedItems.includes(f.id));
            setSelectedItems(selectedFindings);
            setDisputeSubType(result.subType);
            setItemDetails(result.itemDetails || {});
            setBreachSource(result.selectedBreach);
            setCurrentStep(FLOW_STEPS.CONSUMER_IDENTITY);
          }}
          onBack={handleBack}
        />
      );
    
    case FLOW_STEPS.METRO2_SELECTION:
      return (
        <Metro2DisputeSelectionScreen
          findings={findings}
          onContinue={(result) => {
            // Result is an object: { includedItems, itemDetails }
            const selectedFindings = findings.filter(f => result.includedItems?.includes(f.id));
            setSelectedItems(selectedFindings.length > 0 ? selectedFindings : result.includedItems || []);
            setItemDetails(result.itemDetails || {});
            setCurrentStep(FLOW_STEPS.CONSUMER_IDENTITY);
          }}
          onBack={handleBack}
        />
      );
    
    case FLOW_STEPS.CONSUMER_IDENTITY:
      return (
        <ConsumerIdentityStep
          onContinue={(identityData) => {
            setConsumerIdentity(identityData);
            setCurrentStep(FLOW_STEPS.REVIEW_FINALIZE);
          }}
          onBack={() => {
            if (selectedPath === 'factual') {
              setCurrentStep(FLOW_STEPS.FACTUAL_SELECTION);
            } else {
              setCurrentStep(FLOW_STEPS.METRO2_SELECTION);
            }
          }}
          initialData={consumerIdentity}
          reportData={reportData}
        />
      );
    
    case FLOW_STEPS.REVIEW_FINALIZE:
      return (
        <ReviewFinalizeStep
          disputeType={selectedPath}
          disputeSubType={disputeSubType}
          selectedItems={selectedItems}
          itemDetails={itemDetails}
          consumerIdentity={consumerIdentity}
          packages={packages}
          letterPreviews={letterPreviews}
          uploadedDocuments={uploadedDocuments}
          onUploadDocument={handleDocumentUpload}
          onRemoveDocument={handleDocumentRemove}
          onFinalize={handleFinalize}
          onBack={() => setCurrentStep(FLOW_STEPS.CONSUMER_IDENTITY)}
          isLoading={loading}
          simplified={true}
        />
      );
    
    default:
      return null;
  }
};

export default AutoStarterDisputeFlow;
